/**
 * Deterministic PRNG utilities
 * Mulberry32 algorithm with stateful generator
 */

/**
 * Default seed for deterministic RNG
 */
export const DEFAULT_SEED = 0x51756421; // "Qud!" in hex

/**
 * Stateful Mulberry32 PRNG
 * Call next() repeatedly to get sequential random values
 */
export class Random {
  private state: number;

  constructor(seed: number = DEFAULT_SEED) {
    this.state = seed >>> 0; // Ensure unsigned 32-bit
  }

  /**
   * Get next random float in [0, 1)
   */
  next(): number {
    let t = (this.state += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /**
   * Get random integer in [0, max)
   */
  nextInt(max: number): number {
    return Math.floor(this.next() * max);
  }

  /**
   * Get random boolean with given probability
   */
  nextBool(probability: number = 0.5): boolean {
    return this.next() < probability;
  }

  /**
   * Pick random element from array
   */
  choice<T>(array: T[]): T {
    return array[this.nextInt(array.length)];
  }
}

/**
 * Global default Random instance
 * Use this for general-purpose randomization
 */
export const random = new Random();
